import { setHoveredElementKey } from "@/store/iframeSlice";
import _ from "lodash";
import { useRef } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { TempoElement } from "./tempoElement";
import { useIframeStore } from "@/store/zustand/iframeStore";

export const useThrottledSetHoveredElement = () => {
  const dispatch = useDispatch();

  return useRef(
    _.throttle(
      (element?: TempoElement) => {
        if (element) {
          dispatch(setHoveredElementKey(element.getKey()));
        } else {
          dispatch(setHoveredElementKey(null));
        }
      },
      50,
      {
        leading: true,
        trailing: false,
      }
    )
  );
};

export const useThrottledSendSyntheticMouseOverEvent = () => {
  return useRef(
    _.throttle(
      (storyboardId: string, pointX: number, pointY: number, cType: string) => {
        useIframeStore.getState().sendMessageToIFrame(
          {
            type: "executeFunction",
            id: uuidv4(),
            payload: {
              functionName: "syntheticMouseOver",
              args: [
                {
                  x: pointX,
                  y: pointY,
                },
                true,
                true,
              ],
            },
          },
          storyboardId
        );
      },
      50,
      {
        leading: true,
        trailing: false,
      }
    )
  );
};

export const useThrottledSyntheticMouseMove = () => {
  return useRef(
    _.throttle(
      (
        storyboardId: string,
        clientX: number,
        clientY: number,
        buttons?: number
      ) => {
        useIframeStore.getState().sendMessageToIFrame(
          {
            type: "executeFunction",
            id: uuidv4(),
            payload: {
              functionName: "syntheticMouseMove",
              args: [
                {
                  clientX,
                  clientY,
                  buttons,
                },
              ],
            },
          },
          storyboardId
        );
      },
      50,
      {
        leading: true,
        trailing: false,
      }
    )
  );
};

export const sendSyntheticMouseUp = (storyboardId: string) => {
  // Directly post this message to make this whole process faster
  useIframeStore.getState().sendMessageToIFrame(
    {
      type: "executeFunction",
      id: uuidv4(),
      payload: {
        functionName: "syntheticMouseUp",
        args: [{}],
      },
    },
    storyboardId
  );
};

export const useSendDrawingDiv = () => {
  return useRef(
    _.throttle(
      (
        storyboardId: string,
        codebaseIdToAddTo: string,
        indexInParent: number,
        width: number,
        height: number
      ) => {
        // Send the drawing div to get created, or to be resized
        useIframeStore.getState().sendMessageToIFrame(
          {
            type: "executeFunction",
            id: uuidv4(),
            payload: {
              functionName: "tempAddDiv",
              args: [codebaseIdToAddTo, indexInParent, width, height],
            },
          },
          storyboardId
        );
      },
      25,
      {
        leading: true,
        trailing: false,
      }
    )
  );
};

export const getTreeElementLookupToSend = (treeElementLookup: any): any => {
  const treeElementToSend = {};

  Object.keys(treeElementLookup).forEach((codebaseId: any) => {
    const element = treeElementLookup[codebaseId];

    if (!element) {
      treeElementToSend[codebaseId] = null;
      return;
    }

    if (!element) {
      return;
    }

    // Currently the only fields used in the iframe
    treeElementToSend[element.codebaseId] = {
      type: element.type,
      componentName: element.componentName,
      filename: element.filename,
      removableClasses: element.removableClasses,
      staticTextContents: element.staticTextContents,
    };
  });

  return treeElementToSend;
};

// Helper function to format CSS variable names into readable display names
export const formatCssVariableName = (cssVar: string): string => {
  // Extract variable name from var(--variable-name) format
  const varMatch = cssVar.match(/var\(--([^)]+)\)/);
  if (!varMatch) return cssVar;

  const varName = varMatch[1];

  // Convert kebab-case to Title Case
  // e.g., "secondary-n-900" -> "Secondary N 900"
  return varName
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// Helper function to recursively resolve CSS custom properties
export const recursivelyResolveCssVariable = (
  value: string,
  cssCustomProperties: Record<string, string> | null,
  depth = 0
): string => {
  // Prevent infinite recursion
  if (depth > 10) return value;

  // If it's not a CSS variable, return as-is
  if (!value.includes("var(--")) return value;

  // If we don't have CSS custom properties, return as-is
  if (!cssCustomProperties) return value;

  // Extract the variable name from var(--variable-name)
  const varMatch = value.match(/var\(--([^)]+)\)/);
  if (!varMatch) return value;

  const varName = `--${varMatch[1]}`;
  const resolvedValue = cssCustomProperties[varName];

  // If we can't resolve it, return as-is
  if (!resolvedValue) return value;

  // If the resolved value is also a CSS variable, recursively resolve it
  if (resolvedValue.includes("var(--")) {
    return recursivelyResolveCssVariable(
      resolvedValue,
      cssCustomProperties,
      depth + 1
    );
  }

  // Otherwise, return the resolved value
  return resolvedValue;
};

// Helper function to recursively resolve all CSS custom properties in a record
export const resolveAllCssCustomProperties = (
  cssCustomProperties: Record<string, string>
): Record<string, string> => {
  const resolved: Record<string, string> = {};

  // First pass: copy all values
  for (const [key, value] of Object.entries(cssCustomProperties)) {
    resolved[key] = value;
  }

  // Second pass: resolve all variables
  for (const [key, value] of Object.entries(cssCustomProperties)) {
    resolved[key] = recursivelyResolveCssVariable(value, resolved);
  }

  return resolved;
};
